import { CoachingAttemptView } from '@euler/model/coaching';
import { useServiceFactory } from '@euler/services/factory';
import {
  NUMERIC_ALPHABETIC_LOWER_CHARACTERS,
  makeUniqueIdCustom,
} from '@euler/utils/id';
import { useCallback, useRef, useState } from 'react';

/**
 * 再练一次 / 升一级. One request key per choice until it succeeds: a double
 * tap, or a retry after a lost response, is the same attempt.
 */
export function usePracticeAgain(
  attemptId: string,
  onStarted: (attempt: CoachingAttemptView) => void,
) {
  const { coachingService } = useServiceFactory();
  const keys = useRef(new Map<boolean, string>());
  const inFlight = useRef(false);
  const [busy, setBusy] = useState<'again' | 'level_up' | null>(null);
  const [failed, setFailed] = useState(false);
  const start = useCallback(
    async (levelUp: boolean) => {
      if (inFlight.current) return;
      inFlight.current = true;
      const requestKey =
        keys.current.get(levelUp) ??
        makeUniqueIdCustom(NUMERIC_ALPHABETIC_LOWER_CHARACTERS, 24);
      keys.current.set(levelUp, requestKey);
      setBusy(levelUp ? 'level_up' : 'again');
      setFailed(false);
      try {
        const attempt = await coachingService.practiceAgain(attemptId, {
          requestKey,
          levelUp,
        });
        keys.current.delete(levelUp);
        onStarted(attempt);
      } catch (error) {
        console.error('coaching practice again failed', error);
        setFailed(true);
      } finally {
        inFlight.current = false;
        setBusy(null);
      }
    },
    [attemptId, coachingService, onStarted],
  );
  return { start, busy, failed };
}
